import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import './style.scss';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultButton from 'components/Utils/NewDefaultButton';
import AddressFields from 'components/Patient/EditAddress/AddressFields';
import ClinicHours from 'components/Clinic/ClinicHours';
import PerformanceUtils from 'services/PerformanceUtils';
import Collapse from 'components/Utils/Collapse';
import ClinicUtils from 'services/ClinicUtils';
import StackItemsView from 'components/Common/StackItemsView';
import Icon from 'components/Utils/NewIcon';
import IconButton from '@material-ui/core/IconButton';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import UIUtils, { DefaultCheckbox, DefaultSwitch } from 'services/UIUtils';
import MenuItem from '@material-ui/core/MenuItem';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import PhysicianUtils from 'services/PhysicianUtils';
import { useAppSelector } from 'careand-redux/store';
import { PhysicianPhysicianTypeEnum } from 'generated/api';
import StaffUtils from 'services/StaffUtils';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';

const INDEXES = ['clinic', 'home', 'video', 'lab'];
const DEFAULT_VALUES = [
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
  { shut: true },
];

function prepareClinic(clinic = {}) {
  let result = PerformanceUtils.clone(clinic);
  if (!result.hours) {
    result.hours = {};
  }
  INDEXES.forEach((index) => {
    if (!result.hours[index]) result.hours[index] = DEFAULT_VALUES;
  });
  return result;
}

const ClinicDetails = (props) => {
  const dispatch = useAppDispatch();

  const [clinic, setClinic] = useState(prepareClinic(props.clinic));
  console.log(clinic);
  const [collapsed, setCollapsed] = useState(true);
  const [savingClinic, setSavingClinic] = useState(false);

  const physicians = useAppSelector((state) => state.physicians.byId);
  const clinics = useAppSelector((state) => state.resource.clinics);
  const settings = useAppSelector((state) => state.authentication.settings);
  const sesEnabled = !!settings?.modules?.ses?.enabled;
  const sesDomain = settings?.ses_domain;
  const faxEnabled = !!settings?.modules?.fax?.enabled;

  const missingFields = !clinic.address?.latitude || !clinic.address?.longitude;

  const saveClinicDisabled = PerformanceUtils.isEqual(clinic, props.clinic) || missingFields;

  const allPhysicians = useMemo(() => {
    return StaffUtils.filterActiveUsers(Object.values(physicians)).filter(
      ({ physician_type, intern_of }) =>
        !intern_of &&
        (!physician_type || physician_type === PhysicianPhysicianTypeEnum.NursePractitioner),
    );
  }, [physicians]);

  async function saveClinic(event) {
    event.preventDefault();
    event.stopPropagation();
    if (saveClinicDisabled) return;

    const clinicInfo = {
      ...clinic,
      hours: ClinicUtils.prepareHours(clinic.hours),
    };
    setSavingClinic(true);
    await dispatch(api.Resource.setClinicInfo({ clinicInfo }), {
      errorTitle: 'Clinic Info',
    });
    setSavingClinic(false);
  }

  function onClinicChange(event) {
    let name = event.target.name;
    let value = event.target.value;
    let next = { ...clinic, [name]: value };
    // A room that no longer exists can't stay in the rotation pool (the
    // backend rejects a pool room the clinic doesn't have).
    if (name === 'rooms' && clinic.room_rotation?.rooms?.length) {
      next.room_rotation = {
        ...clinic.room_rotation,
        rooms: clinic.room_rotation.rooms.filter((room) => (value || []).includes(room)),
      };
    }
    setClinic(next);
  }

  function onAddressChange(event) {
    let { name, value } = event.target;
    let address = { ...(clinic.address || {}), [name]: value };
    setClinic({ ...clinic, address });
  }

  function setAddress(address) {
    setClinic({ ...clinic, address });
  }

  function onConfigurationChange(event) {
    let { name, value } = event.target;
    let clinic_configuration = { ...(clinic.clinic_configuration || {}), [name]: value };
    setClinic({ ...clinic, clinic_configuration });
  }

  async function toggleCollapsed() {
    setCollapsed(!collapsed);
  }

  return (
    <div className="details-panel clinic-details scroll-list">
      <form style={{ width: '100%', maxWidth: '800px' }} onSubmit={saveClinic}>
        {/* Basic Information Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="hospital" height="20px" className="section-icon" />
            <span className="section-title">Basic Information</span>
          </div>
          <div className="section-content">
            <DefaultTextField
              name="name"
              label="Clinic Name"
              value={clinic.name}
              onChange={onClinicChange}
            />
            <div className="field-row">
              <DefaultTextField
                name="phone"
                label="Phone"
                value={clinic.phone}
                onChange={onClinicChange}
              />
              <DefaultTextField
                name="fax"
                label="Fax"
                value={clinic.fax}
                onChange={onClinicChange}
              />
            </div>
            <div className="field-row">
              <DefaultTextField
                name="email"
                label="Email"
                value={clinic.email}
                onChange={onClinicChange}
              />
              <DefaultTextField
                name="website"
                label="Website"
                value={clinic.website}
                onChange={onClinicChange}
              />
            </div>
            {sesEnabled && sesDomain && (
              <div className="email-prefix-field">
                <DefaultTextField
                  name="email_prefix"
                  label="Results Email Prefix"
                  value={clinic.email_prefix || ''}
                  onChange={onClinicChange}
                />
                <span className="email-preview">
                  {clinic.email_prefix || 'results'}.{clinic.tenant_id}@{sesDomain}
                </span>
                <span className="field-helper-text">
                  Emails sent to this address will appear as documents in this clinic's inbox
                </span>
              </div>
            )}
            <div className="field-row">
              <DefaultTextField
                name="google_url"
                label="Google My Business URL"
                value={clinic.google_url}
                onChange={onClinicChange}
              />
              <DefaultTextField
                name="google_maps_url"
                label="Google Maps URL"
                value={clinic.google_maps_url}
                onChange={onClinicChange}
              />
            </div>
            <div className="field-row">
              <DefaultDatePicker
                name="date_start"
                label="Date Started"
                value={clinic.date_start}
                onChange={(e, value) => onClinicChange({ target: { name: 'date_start', value } })}
              />
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <DefaultSwitch
                  name="hide"
                  className="red"
                  padded={false}
                  onChange={(event, isInputChecked) => {
                    setClinic({ ...clinic, hide: isInputChecked });
                  }}
                  label="Hide From Patients"
                  checked={!!clinic.hide}
                />
              </div>
            </div>
            <div className="forward-to-clinic-field">
              <DefaultSelectField
                id="forward_to_clinic_id-field"
                style={{ ...UIUtils.SELECT_FIELD_STYLE }}
                label="Forward faxes & documents to"
                name="forward_to_clinic_id"
                value={clinic.forward_to_clinic_id || ''}
                onChange={onClinicChange}
                inputElements={[
                  <MenuItem key="none" value="">
                    None
                  </MenuItem>,
                  // Single hop: a clinic that itself forwards is not a target.
                  ...clinics
                    .filter(
                      (other) =>
                        other.id !== clinic.id &&
                        (!other.forward_to_clinic_id || other.id === clinic.forward_to_clinic_id),
                    )
                    .map((other) => (
                      <MenuItem key={other.id} value={other.id}>
                        {other.name}
                      </MenuItem>
                    )),
                ]}
              />
              <span className="field-helper-text">
                Inbound faxes, results emails and new documents for this clinic land in the chosen
                clinic's inbox instead. Use it when closing a clinic.
              </span>
            </div>
          </div>
        </div>

        {/* Address Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="location-dot" height="20px" className="section-icon" />
            <span className="section-title">Address</span>
          </div>
          <div className="section-content">
            <AddressFields
              address={clinic.address || {}}
              onChange={onAddressChange}
              onSearchResult={setAddress}
              style={{ width: '100%', marginRight: 0 }}
              includeLatLng={true}
            />
          </div>
        </div>

        {/* Clinical Director Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="user-nurse" height="20px" className="section-icon" />
            <span className="section-title">Clinical Director</span>
          </div>
          <div className="section-content">
            <DefaultSelectField
              id="clinical_director_id-field"
              style={{ ...UIUtils.SELECT_FIELD_STYLE }}
              label="Clinical Director"
              name="clinical_director_id"
              value={clinic.clinical_director_id}
              onChange={onClinicChange}
              inputElements={allPhysicians.map((physician) => (
                <MenuItem key={physician.id} value={physician.id}>
                  {PhysicianUtils.getName(physician)}
                </MenuItem>
              ))}
            />
          </div>
        </div>

        {/* Message Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="message" height="20px" className="section-icon" />
            <span className="section-title">Clinic Message</span>
          </div>
          <div className="section-content">
            <TextareaAutosize
              cacheMeasurements
              name="message"
              value={clinic.message}
              onChange={onClinicChange}
            />
          </div>
        </div>

        {/* Rooms Section */}
        <div className="clinic-section rooms-section">
          <div className="section-header">
            <Icon src="door-open" height="20px" className="section-icon" />
            <span className="section-title">Rooms</span>
          </div>
          <div className="section-content">
            <Rooms rooms={clinic.rooms} onChange={onClinicChange} />
          </div>
        </div>

        {/* Room Rotation Section */}
        <div className="clinic-section rooms-section">
          <div className="section-header">
            <Icon src="door-open" height="20px" className="section-icon" />
            <span className="section-title">Daily NP Room Rotation</span>
          </div>
          <div className="section-content">
            <RoomRotation
              rooms={clinic.rooms}
              rotation={clinic.room_rotation}
              onChange={onClinicChange}
            />
          </div>
        </div>

        {/* Booking Resources Section */}
        <div className="clinic-section rooms-section">
          <div className="section-header">
            <Icon src="door-open" height="20px" className="section-icon" />
            <span className="section-title">Booking Resources</span>
          </div>
          <div className="section-content">
            <BookingResources resources={clinic.booking_resources} onChange={onClinicChange} />
          </div>
        </div>

        {/* Hours Section */}
        <div className="clinic-section">
          <div className="section-header">
            <Icon src="clock" height="20px" className="section-icon" />
            <span className="section-title">Operating Hours</span>
          </div>
          <div className="section-content">
            <ClinicHours
              hours={clinic.hours}
              onChange={(hours) => setClinic({ ...clinic, hours })}
            />
          </div>
        </div>

        {/* Technical Configuration */}
        <Collapse title="Technical Configuration" collapsed={collapsed} onClick={toggleCollapsed}>
          <div className="clinic-section" style={{ marginTop: '10px' }}>
            <div className="section-content">
              {clinic.clinic_configuration !== undefined && (
                <ClinicConfiguration
                  configuration={clinic.clinic_configuration}
                  onChange={onConfigurationChange}
                />
              )}
            </div>
          </div>
        </Collapse>

        {/* Save Button */}
        <div className="save-button-wrapper">
          <DefaultButton
            name="Save Clinic Info"
            onClick={saveClinic}
            disabled={saveClinicDisabled}
            loading={savingClinic}
            className="blue"
          />
        </div>
      </form>
    </div>
  );
};

const Rooms = ({ rooms = [], onChange }) => {
  const [room, setRoom] = useState('');

  function changeRooms(value) {
    let event = {
      target: {
        name: 'rooms',
        value,
      },
    };
    onChange(event);
  }

  function onMoveEnd(value) {
    changeRooms(value.map(({ id }) => id));
  }

  function deleteItem(item) {
    let value = [...rooms].filter((room) => room !== item?.id);
    changeRooms(value);
  }

  function addRoom() {
    let value = [...rooms, room];
    changeRooms(value);
    setRoom('');
  }

  return (
    <>
      {rooms.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <StackItemsView
            stack={rooms.map((room) => {
              return { id: room };
            })}
            onDeleteItem={deleteItem}
            describeItem={(room) => room.id}
            onMoveEnd={onMoveEnd}
          />
        </div>
      )}
      <div className="room-add-controls">
        <DefaultTextField
          name="room"
          label="Add Room"
          value={room}
          onChange={(event) => setRoom(event.target.value)}
          style={{ flex: 1, maxWidth: '250px' }}
        />
        <IconButton
          onClick={addRoom}
          disabled={!room}
          className="blue"
          size="small"
          style={{ width: '40px', height: '40px' }}
        >
          <Icon src="plus" className={room ? 'white' : 'grey'} showTooltip={true} alt="Add room" />
        </IconButton>
      </div>
    </>
  );
};

const RoomRotation = ({ rooms = [], rotation, onChange }) => {
  const enabled = !!rotation?.enabled;
  const pool = rotation?.rooms || [];
  const uniqueRooms = [...new Set(rooms)];

  function emit(changes) {
    onChange({ target: { name: 'room_rotation', value: { enabled, rooms: pool, ...changes } } });
  }

  function toggleRoom(room) {
    const next = pool.includes(room)
      ? pool.filter((it) => it !== room)
      : uniqueRooms.filter((it) => it === room || pool.includes(it)); // keep the clinic's order
    emit({ rooms: next });
  }

  return (
    <>
      <DefaultSwitch
        name="room_rotation_enabled"
        padded={false}
        label="Daily NP room rotation"
        checked={enabled}
        onChange={(event, isInputChecked) => emit({ enabled: isInputChecked })}
      />
      {uniqueRooms.length > 0 ? (
        <div className="room-rotation-pool">
          {uniqueRooms.map((room, index) => (
            <DefaultCheckbox
              key={room}
              name={`room-rotation-${index}`}
              id={`room-rotation-${index}`}
              value={pool.includes(room)}
              onChange={() => toggleRoom(room)}
              text={room}
              wrapperClassName="room-rotation-room"
            />
          ))}
        </div>
      ) : (
        <span className="field-helper-text">Add rooms above to choose the rotation pool.</span>
      )}
      <span className="field-helper-text">
        Each day, 30 min before the clinic opens, in-clinic NPs are randomly assigned one of these
        rooms and notified by SMS.
        {enabled && pool.length === 0 && ' Select at least one room for the rotation to run.'}
      </span>
    </>
  );
};

const SLUG_RE = /^[a-z0-9_-]+$/;
const toSlug = (s) =>
  String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const BookingResources = ({ resources = [], onChange }) => {
  const [draft, setDraft] = useState({ name: '', id: '', count: 1 });

  function emit(value) {
    onChange({ target: { name: 'booking_resources', value } });
  }

  function onDraftName(name) {
    setDraft((d) => ({ ...d, name, id: d.idTouched ? d.id : toSlug(name) }));
  }

  function addResource() {
    emit([
      ...resources,
      { id: draft.id, name: draft.name.trim(), count: parseInt(draft.count, 10) },
    ]);
    setDraft({ name: '', id: '', count: 1 });
  }

  function updateCount(id, count) {
    emit(resources.map((r) => (r.id === id ? { ...r, count: parseInt(count, 10) || 1 } : r)));
  }

  function removeResource(id) {
    emit(resources.filter((r) => r.id !== id));
  }

  const idTaken = resources.some((r) => r.id === draft.id);
  const canAdd =
    draft.name.trim() && SLUG_RE.test(draft.id) && !idTaken && parseInt(draft.count, 10) >= 1;

  return (
    <>
      {resources.map((r) => (
        <div key={r.id} className="room-add-controls" style={{ marginBottom: '8px' }}>
          <span style={{ flex: 1 }}>
            {r.name} <span style={{ color: '#888' }}>({r.id})</span>
          </span>
          <DefaultTextField
            name={`count_${r.id}`}
            label="Count"
            type="number"
            value={r.count}
            onChange={(e) => updateCount(r.id, e.target.value)}
            style={{ width: '90px' }}
          />
          <IconButton
            onClick={() => removeResource(r.id)}
            size="small"
            style={{ width: '40px', height: '40px' }}
          >
            <Icon src="trash" className="grey" showTooltip={true} alt="Remove resource" />
          </IconButton>
        </div>
      ))}
      <div className="room-add-controls">
        <DefaultTextField
          name="resource_name"
          label="Resource name"
          value={draft.name}
          onChange={(e) => onDraftName(e.target.value)}
          style={{ flex: 1, maxWidth: '200px' }}
        />
        <DefaultTextField
          name="resource_id"
          label="ID"
          value={draft.id}
          error={!!draft.id && (!SLUG_RE.test(draft.id) || idTaken)}
          onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value, idTouched: true }))}
          style={{ width: '140px' }}
        />
        <DefaultTextField
          name="resource_count"
          label="Count"
          type="number"
          value={draft.count}
          onChange={(e) => setDraft((d) => ({ ...d, count: e.target.value }))}
          style={{ width: '90px' }}
        />
        <IconButton
          onClick={addResource}
          disabled={!canAdd}
          className="blue"
          size="small"
          style={{ width: '40px', height: '40px' }}
        >
          <Icon
            src="plus"
            className={canAdd ? 'white' : 'grey'}
            showTooltip={true}
            alt="Add resource"
          />
        </IconButton>
      </div>
      <div style={{ fontWeight: 100, color: '#b4b4b4', fontSize: '12px', marginTop: '6px' }}>
        Countable things a booking occupies (IV chairs, bloodwork chair). Concerns pick one under
        &quot;Booking resource&quot;; overlapping bookings are capped at Count. The ID cannot change
        after adding.
      </div>
    </>
  );
};

const ClinicConfiguration = ({ configuration = {}, onChange }) => {
  let result = [];
  for (let key in configuration) {
    if (key !== 'id') {
      if (key === 'fax_cover')
        result.push(
          <div key={key} style={{ width: '100%' }}>
            <span className="small-title">{key}</span>
            <TextareaAutosize
              cacheMeasurements
              name={key}
              value={configuration[key]}
              onChange={onChange}
            />
          </div>,
        );
      else
        result.push(
          <DefaultTextField
            key={key}
            name={key}
            label={key}
            value={configuration[key]}
            onChange={onChange}
          />,
        );
    }
  }
  return result;
};

ClinicDetails.propTypes = {
  clinic: PropTypes.object.isRequired,
};

export default ClinicDetails;
